import { useEffect, type FC } from 'react';
import { useRoomContext } from '@livekit/components-react';
import {
  RoomEvent,
  Track,
  VideoQuality,
  type RemoteParticipant,
  type RemoteTrackPublication,
} from 'livekit-client';
import { getPresetFromMetadata, isInClassBotIdentity } from 'adapters/LiveKitMeetingAdapter';
import { PRESET_TYPE } from 'configs/meeting';

/**
 * Recorder only: subscribe to the low simulcast layer (320x180) of student cameras.
 * Their tiles are ~100 px in the 854x480 output, so the top layer (up to 1080p30 in
 * notebook mode) was decoded by Chrome for nothing. Tutor camera and screen share
 * keep the top layer. Parent/TM viewers of this page are untouched.
 */
export const isStudentCamera = (pub: RemoteTrackPublication, participant: RemoteParticipant): boolean =>
  pub.source === Track.Source.Camera
  && !isInClassBotIdentity(participant.identity)
  && getPresetFromMetadata(participant.metadata) !== PRESET_TYPE.TUTOR;

export const useRecorderStudentLayers = (): void => {
  const room = useRoomContext();

  useEffect(() => {
    const lower = (pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      if (!isInClassBotIdentity(room.localParticipant.identity)) return;
      if (isStudentCamera(pub, participant)) pub.setVideoQuality(VideoQuality.LOW);
    };
    const lowerAll = () => {
      room.remoteParticipants.forEach((p) => p.trackPublications.forEach((pub) => lower(pub, p)));
    };
    const onSubscribed = (_t: unknown, pub: RemoteTrackPublication, p: RemoteParticipant) => lower(pub, p);

    lowerAll();
    room.on(RoomEvent.TrackSubscribed, onSubscribed);
    // Identity is only known once connected; metadata can arrive after the track.
    room.on(RoomEvent.Connected, lowerAll);
    room.on(RoomEvent.ParticipantMetadataChanged, lowerAll);
    return () => {
      room.off(RoomEvent.TrackSubscribed, onSubscribed);
      room.off(RoomEvent.Connected, lowerAll);
      room.off(RoomEvent.ParticipantMetadataChanged, lowerAll);
    };
  }, [room]);
};

/** Headless controller — render inside <LiveKitRoom>. Renders nothing. */
const RecorderStudentLayers: FC = () => {
  useRecorderStudentLayers();
  return null;
};

export default RecorderStudentLayers;
